import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { request, setUnauthorizedHandler } from '../../api/http';
import { clearUser, setSessionReady, setUser } from '../../state-management/slices/Global';
import { RootState } from '../../state-management/store';
import type { IUser } from '../../types/user';

/** How often an open tab asks whether the session cookie is still valid. */
const SESSION_RECHECK_MS = 30_000;

/**
 * Restores Redux user from the API session cookie on full page load / refresh.
 * `sessionReady` stays false until that check finishes, so protected routes
 * do not treat a still-loading session as logged out.
 * A later 401, or a failed recheck while the tab stays open, clears the user
 * and the protected route returns to the login screen.
 */
const SessionBootstrap: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const dispatch = useDispatch();
	const userId = useSelector((state: RootState) => state.global.user?.id ?? null);

	useEffect(() => {
		setUnauthorizedHandler(() => {
			dispatch(clearUser());
		});
		return () => setUnauthorizedHandler(null);
	}, [dispatch]);

	useEffect(() => {
		let cancelled = false;

		const loadSession = async (): Promise<void> => {
			try {
				const user = await request<IUser>('/api/auth/me');
				if (!cancelled) dispatch(setUser(user));
			} catch {
				/* 401 or network error — the visitor stays logged out */
			} finally {
				if (!cancelled) dispatch(setSessionReady());
			}
		};

		void loadSession();
		return () => {
			cancelled = true;
		};
	}, [dispatch]);

	useEffect(() => {
		if (userId === null) return;

		let lastCheck = Date.now();
		const recheck = (): void => {
			if (document.visibilityState === 'hidden') return;
			const now = Date.now();
			if (now - lastCheck < 5_000) return;
			lastCheck = now;
			void request<IUser>('/api/auth/me').catch(() => {
				/* 401 clears the user; a network error leaves the screen as it is */
			});
		};

		document.addEventListener('visibilitychange', recheck);
		window.addEventListener('focus', recheck);
		const timer = window.setInterval(recheck, SESSION_RECHECK_MS);
		return () => {
			document.removeEventListener('visibilitychange', recheck);
			window.removeEventListener('focus', recheck);
			window.clearInterval(timer);
		};
	}, [userId]);

	return <>{children}</>;
};

export default SessionBootstrap;
